/**
 * Client portal « Appels » (W5, B3-ClientCalls, QW-h5, QW-h10, DES-11, DES-20).
 *
 * The calls made for the client, as given by ClientDashboard (its period and
 * filters) or by the dialer's read-only preview (ClientPortalScreen, `inert`):
 *   - 4 figures on those calls (VFStatCard: conversion = meetings / connected,
 *     « — » without a connected call, DES-19; no trend: there is no previous
 *     period here);
 *   - search, outcome and agent filters shown as pills;
 *   - a paged table (DataTable, 25 rows a page, in an overflow-x container)
 *     instead of a staggered, hover-scaling list (DES-20);
 *   - « Exporter » downloads the filtered calls (exportCsv);
 *   - a row opens its detail: prospect, outcome, notes, AI analysis,
 *     recording. The Twilio block (SID, price) is gone: it is the agency's
 *     data, and /twilio/calls is super admin only, which made this tab show
 *     « Erreur de chargement » to every client (PORTAIL-4).
 * REV-W5-1: `load` says whether the page's calls are loading or failed:
 * skeleton tiles and rows, or « Indisponible » and the table's error with
 * « Réessayer », never 0 and « Aucun appel ».
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Phone, Play, Search, ChevronRight } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable, type DataTableColumn, type FilterPill, type SortState } from '@/components/shared/DataTable';
import { VFStatCard } from '@/components/vf/StatCard';
import { StatusBadge } from '@/components/xdial/Badge';
import { api } from '@/api/api-client';
import { toCsv, downloadCsv } from '@/lib/exportCsv';
import { outcomeLabel, isKnownOutcome } from '@/services/dialer/callOutcomeLabels';
import type { ProspectStatusKey } from '@/theme/tokens';
import { callAgent, callDate, callFigures, callOutcome, formatDuration, type PortalCall, READY, type SectionLoad } from './portalFigures';

export const CALLS_PAGE_SIZE = 25;

const OUTCOME_FILTERS = [
  'meeting_booked', 'callback_scheduled', 'contacted', 'not_interested', 'no_answer', 'gatekeeper', 'wrong_number', 'not_reachable',
] as const;

interface CallRow extends PortalCall {
  sid?: string;
  external_call_id?: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_title?: string | null;
  prospect_phone?: string | null;
  to?: string | null;
  call_notes?: string | null;
  notes?: string | null;
  recording_url?: string | null;
  ai_summary?: string | null;
  ai_action_items?: string[] | null;
}

interface CallsSectionProps {
  calls?: CallRow[];
  /** Kept for ClientDashboard; the section no longer fetches by email. */
  clientEmail?: string;
  onPlayRecording?: (url: string, label?: string) => void;
  currentAudioSrc?: string | null;
  /** Loading / failed state of `calls` (REV-W5-1); ready when absent. */
  load?: SectionLoad;
}

interface Analysis { summary?: string; action_items?: string[] }
interface AiApi { functions: { invoke: (name: string, params: Record<string, unknown>) => Promise<{ data?: { analysis?: Analysis } }> } }

const rowId = (c: CallRow, i: number) => c.id || c.sid || c.external_call_id || `row-${i}`;

export default function CallsSection({ calls = [], onPlayRecording, currentAudioSrc, load = READY }: CallsSectionProps) {
  const { t, i18n } = useTranslation(['admin', 'dialer', 'common']);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [sort, setSort] = useState<SortState | null>({ id: 'date', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<CallRow | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const rows = useMemo(() => calls.map((c, i) => ({ ...c, id: rowId(c, i) })), [calls]);
  const figures = useMemo(() => callFigures(rows), [rows]);
  const agents = useMemo(() => Array.from(new Set(rows.map(callAgent).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = rows.filter((c) => {
      if (q && !`${c.prospect_name || ''} ${c.prospect_company || ''}`.toLowerCase().includes(q)) return false;
      if (outcomeFilter !== 'all' && callOutcome(c) !== outcomeFilter) return false;
      if (agentFilter !== 'all' && callAgent(c) !== agentFilter) return false;
      return true;
    });
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const key = (c: CallRow) => (sort.id === 'duration' ? c.duration_seconds || 0 : callDate(c)?.getTime() || 0);
      out.sort((a, b) => (key(a) - key(b)) * dir);
    }
    return out;
  }, [rows, search, outcomeFilter, agentFilter, sort]);

  const pageRows = filtered.slice(page * CALLS_PAGE_SIZE, (page + 1) * CALLS_PAGE_SIZE);
  const resetPage = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setPage(0); };

  const pills: FilterPill[] = [
    ...(search.trim() ? [{ id: 'search', label: `« ${search.trim()} »`, onRemove: () => resetPage(setSearch)('') }] : []),
    ...(outcomeFilter !== 'all' ? [{ id: 'outcome', label: outcomeLabel(outcomeFilter, t), onRemove: () => resetPage(setOutcomeFilter)('all') }] : []),
    ...(agentFilter !== 'all' ? [{ id: 'agent', label: agentFilter, onRemove: () => resetPage(setAgentFilter)('all') }] : []),
  ];

  const label = (c: CallRow) => {
    const d = callDate(c);
    return `${c.prospect_name || t('admin:backoffice.calls.genericCallLabel')}${d ? ` · ${d.toLocaleDateString(i18n.language)}` : ''}`;
  };

  const exportCalls = () => {
    if (filtered.length === 0) return;
    const headers = t('admin:backoffice.calls.csv.headers').split(',');
    const csvRows = filtered.map((c) => {
      const d = callDate(c);
      const o = callOutcome(c);
      return [
        d ? moment(d).format('DD/MM/YYYY HH:mm') : '',
        c.prospect_name || '',
        c.prospect_company || '',
        c.prospect_phone || c.to || '',
        callAgent(c),
        isKnownOutcome(o) ? outcomeLabel(o, t) : o,
        c.duration_seconds || 0,
        c.call_notes || c.notes || '',
      ];
    });
    downloadCsv(`appels_${moment().format('YYYY-MM-DD')}.csv`, toCsv(headers, csvRows));
    toast.success(t('admin:backoffice.calls.exportDone'));
  };

  const analyze = async () => {
    if (!selected?.id || selected._source !== 'ParallelDialer') return;
    setAiLoading(true);
    try {
      const { data } = await (api as unknown as AiApi).functions.invoke('analyzeCallWithAI', {
        callId: selected.id,
        callNotes: selected.call_notes || selected.notes || '',
        prospectName: selected.prospect_name,
        prospectCompany: selected.prospect_company,
        outcome: callOutcome(selected),
      });
      const a = data?.analysis;
      // Shown to the client only: the portal is read-only on calls.
      if (a) setSelected((prev) => (prev ? { ...prev, ai_summary: a.summary, ai_action_items: a.action_items } : prev));
      toast.success(t('admin:backoffice.calls.aiAnalysisDone'));
    } catch {
      toast.error(t('admin:backoffice.calls.aiAnalysisFailed'));
    } finally {
      setAiLoading(false);
    }
  };

  const columns: DataTableColumn<CallRow>[] = [
    {
      id: 'date', header: t('admin:backoffice.calls.columns.date'), sortable: true,
      cell: (c) => { const d = callDate(c); return <span className="whitespace-nowrap tabular-nums">{d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'}</span>; },
    },
    {
      id: 'prospect', header: t('admin:backoffice.calls.columns.prospect'),
      cell: (c) => (
        <div className="min-w-0">
          <div className="font-medium text-[var(--vf-text-1)]">{c.prospect_name || '—'}</div>
          {c.prospect_title && <div className="text-[12px] text-[var(--vf-text-3)]">{c.prospect_title}</div>}
        </div>
      ),
    },
    { id: 'company', header: t('admin:backoffice.calls.columns.company'), hideable: true, cell: (c) => c.prospect_company || '—' },
    { id: 'agent', header: t('admin:backoffice.calls.detail.agent'), hideable: true, cell: (c) => callAgent(c) || '—' },
    {
      id: 'outcome', header: t('admin:backoffice.calls.outcomePlaceholder'),
      cell: (c) => { const o = callOutcome(c); return o ? <StatusBadge status={o as ProspectStatusKey} /> : '—'; },
    },
    { id: 'duration', header: t('admin:backoffice.calls.columns.duration'), sortable: true, align: 'right', cell: (c) => <span className="tabular-nums">{formatDuration(c.duration_seconds)}</span> },
    {
      id: 'recording', header: t('admin:backoffice.calls.detail.recording'), hideable: true,
      cell: (c) => (c.recording_url ? (
        <Button
          type="button" variant="outline" size="sm" className="min-h-11"
          aria-pressed={currentAudioSrc === c.recording_url}
          onClick={(e) => { e.stopPropagation(); onPlayRecording?.(c.recording_url!, label(c)); }}
        >
          <Play className="mr-1 h-4 w-4" aria-hidden />
          {currentAudioSrc === c.recording_url ? t('admin:backoffice.calls.playing') : t('admin:backoffice.calls.play')}
        </Button>
      ) : '—'),
    },
    {
      id: 'open', header: <span className="sr-only">{t('admin:backoffice.calls.detail.title')}</span>, align: 'right',
      cell: (c) => (
        <Button
          type="button" variant="ghost" size="icon" className="h-11 w-11"
          aria-label={t('admin:backoffice.calls.openDetail', { name: c.prospect_name || t('admin:backoffice.calls.genericCallLabel') })}
          onClick={(e) => { e.stopPropagation(); setSelected(c); }}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
  ];

  const hint = t('admin:backoffice.calls.figuresHint');
  const ready = load.status === 'ready';
  const notes = selected ? selected.call_notes || selected.notes || '' : '';

  return (
    <div className="min-w-0 space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <VFStatCard state={load.status} label={t('admin:backoffice.calls.stats.total')} metric={{ current: figures.calls, kind: 'count' }} hint={hint} />
        <VFStatCard state={load.status} label={t('admin:backoffice.calls.stats.meetingsBooked')} metric={{ current: figures.meetings, kind: 'count' }} hint={hint} />
        <VFStatCard state={load.status} label={t('admin:backoffice.calls.stats.callbacks')} metric={{ current: figures.callbacks, kind: 'count' }} hint={hint} />
        <VFStatCard
          state={load.status}
          label={t('admin:backoffice.calls.stats.conversionRate')}
          metric={{ current: figures.conversion, kind: 'rate' }}
          hint={t('admin:backoffice.calls.conversionHint', { meetings: figures.meetings, connected: figures.connected })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-56 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--vf-text-3)]" aria-hidden />
          <Input
            aria-label={t('admin:backoffice.calls.searchPlaceholder')}
            placeholder={t('admin:backoffice.calls.searchPlaceholder')}
            value={search}
            onChange={(e) => resetPage(setSearch)(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={outcomeFilter} onValueChange={resetPage(setOutcomeFilter)}>
          <SelectTrigger className="w-full sm:w-44" aria-label={t('admin:backoffice.calls.outcomePlaceholder')}>
            <SelectValue placeholder={t('admin:backoffice.calls.outcomePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin:backoffice.calls.allOutcomes')}</SelectItem>
            {OUTCOME_FILTERS.map((key) => <SelectItem key={key} value={key}>{outcomeLabel(key, t)}</SelectItem>)}
          </SelectContent>
        </Select>
        {agents.length > 1 && (
          <Select value={agentFilter} onValueChange={resetPage(setAgentFilter)}>
            <SelectTrigger className="w-full sm:w-48" aria-label={t('admin:backoffice.calls.agentPlaceholder')}>
              <SelectValue placeholder={t('admin:backoffice.calls.agentPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin:backoffice.calls.allAgents')}</SelectItem>
              {agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="min-h-11 sm:ml-auto" onClick={exportCalls} disabled={!ready || filtered.length === 0}>
          <Download className="mr-2 h-4 w-4" aria-hidden />
          {t('admin:backoffice.calls.exportButton')}
        </Button>
      </div>

      <DataTable<CallRow>
        caption={t('admin:backoffice.calls.caption')}
        columns={columns}
        rows={pageRows}
        getRowId={(c) => c.id!}
        status={load.status}
        error={{ message: t('admin:backoffice.calls.loadError'), code: load.code, onRetry: () => load.onRetry?.() }}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(0); }}
        filters={pills}
        onClearFilters={() => { setSearch(''); setOutcomeFilter('all'); setAgentFilter('all'); setPage(0); }}
        empty={{ title: (
          <span className="inline-flex flex-col items-center gap-2">
            <Phone className="h-6 w-6 text-[var(--vf-text-3)]" aria-hidden />
            {t('admin:backoffice.calls.noCalls')}
          </span>
        ) }}
        pagination={ready && filtered.length > CALLS_PAGE_SIZE ? { page, pageSize: CALLS_PAGE_SIZE, total: filtered.length, onPageChange: setPage } : undefined}
        onRowClick={setSelected}
        storageKey="portal-calls"
      />

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin:backoffice.calls.detail.title')}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5 text-sm">
              <section>
                <h3 className="mb-2 font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.calls.detail.prospect')}</h3>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.name')}</dt><dd>{selected.prospect_name || '—'}</dd>
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.company')}</dt><dd>{selected.prospect_company || '—'}</dd>
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.title_')}</dt><dd>{selected.prospect_title || '—'}</dd>
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.phone')}</dt><dd className="font-vf-mono">{selected.prospect_phone || selected.to || '—'}</dd>
                </dl>
              </section>
              <section>
                <h3 className="mb-2 font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.calls.detail.outcomeTitle')}</h3>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.currentOutcome')}</dt>
                  <dd>{callOutcome(selected) ? <StatusBadge status={callOutcome(selected) as ProspectStatusKey} /> : '—'}</dd>
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.agent')}</dt><dd>{callAgent(selected) || '—'}</dd>
                  <dt className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.columns.duration')}</dt><dd className="tabular-nums">{formatDuration(selected.duration_seconds)}</dd>
                </dl>
                <p className="mt-2 whitespace-pre-wrap text-[var(--vf-text-1)]">{notes || t('admin:backoffice.calls.detail.noNotes')}</p>
              </section>
              <section>
                <h3 className="mb-2 font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.calls.detail.aiAnalysis')}</h3>
                {selected.ai_summary ? (
                  <div className="space-y-2">
                    <p className="whitespace-pre-wrap">{selected.ai_summary}</p>
                    {Array.isArray(selected.ai_action_items) && selected.ai_action_items.length > 0 && (
                      <ul className="list-disc pl-5">{selected.ai_action_items.map((it, i) => <li key={i}>{it}</li>)}</ul>
                    )}
                  </div>
                ) : (
                  <p className="text-[var(--vf-text-3)]">{t('admin:backoffice.calls.detail.noAnalysis')}</p>
                )}
                {/* i18n-exempt: a call source identifier, not text */}
                {selected._source === 'ParallelDialer' && notes && (
                  <div className="mt-3 flex justify-end">
                    <Button onClick={() => { void analyze(); }} disabled={aiLoading}>
                      {aiLoading ? t('admin:backoffice.calls.detail.analyzing') : t('admin:backoffice.calls.detail.analyzeWithAi')}
                    </Button>
                  </div>
                )}
              </section>
              {selected.recording_url && (
                <section>
                  <h3 className="mb-2 font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.calls.detail.recording')}</h3>
                  <Button size="sm" className="mb-3 min-h-11" onClick={() => onPlayRecording?.(selected.recording_url!, label(selected))}>
                    <Play className="mr-1 h-4 w-4" aria-hidden /> {t('admin:backoffice.calls.detail.playInPlayer')}
                  </Button>
                  <audio controls className="w-full" src={selected.recording_url} />
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
